import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom';
import { venuesService } from '../../services/venuesService';
import ResourceSidebar from '../Shared/ResourceSidebar';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './VenuesPage.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const TWZ_LOCATIONS = ['New_York', 'North_America', 'World', 'Greece', 'London'];
const EMPTY_VENUE_FORM = {
  name: '',
  location: '',
  country: '',
  capacity: '',
  twz_location: '',
};

const VenuesPage = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [aliasPanel, setAliasPanel] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [aliasLoading, setAliasLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_VENUE_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [mergePanel, setMergePanel] = useState(null);
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeResults, setMergeResults] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');

  const loadVenues = async (qValue = '') => {
    setLoading(true);
    setError('');
    const result = await venuesService.listVenues({
      q: qValue || undefined,
      limit: 5000,
    });

    if (result.success) {
      setVenues(result.items || []);
      setTotalCount(result.totalCount || 0);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadVenues('');
  }, []);

  useEffect(() => {
    if (!contextMenu) {
      return undefined;
    }

    const closeMenu = () => setContextMenu(null);
    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!mergePanel || !mergeQuery.trim()) {
      setMergeResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setMergeLoading(true);
      const result = await venuesService.listVenues({
        q: mergeQuery.trim(),
        limit: 20,
      });
      setMergeLoading(false);

      if (!result.success) {
        setMergeError(result.error);
        setMergeResults([]);
        return;
      }

      setMergeError('');
      setMergeResults((result.items || []).filter((item) => item.venue_id !== mergePanel.venue.venue_id));
    }, 250);

    return () => clearTimeout(timer);
  }, [mergePanel, mergeQuery]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Venue Name',
      field: 'name',
      minWidth: 260,
      flex: 1.6,
      editable: true,
      pinned: 'left',
    },
    {
      headerName: 'Location',
      field: 'location',
      minWidth: 150,
      editable: true,
    },
    {
      headerName: 'Country',
      field: 'country',
      minWidth: 150,
      editable: true,
    },
    {
      headerName: 'Capacity',
      field: 'capacity',
      minWidth: 120,
      editable: true,
      valueParser: (params) => {
        if (params.newValue === '' || params.newValue == null) return null;
        const parsed = Number(params.newValue);
        return Number.isNaN(parsed) ? params.oldValue : Math.trunc(parsed);
      },
    },
    {
      headerName: 'Aliases',
      field: 'alias_count',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'Events',
      field: 'event_count',
      minWidth: 110,
      editable: false,
    },
    {
      headerName: 'TWZ Location',
      field: 'twz_location',
      minWidth: 150,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: TWZ_LOCATIONS,
      },
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
  }), []);

  const handleFilter = (event) => {
    event.preventDefault();
    loadVenues(query.trim());
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_VENUE_FORM);
    setCreateError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createSaving) return;
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleCreateVenue = async (event) => {
    event.preventDefault();
    const name = createForm.name.trim();
    const location = createForm.location.trim();
    const country = createForm.country.trim();

    if (!name || !location || !country) {
      setCreateError('Venue name, Location, and Country are required.');
      return;
    }

    const capacity = createForm.capacity === '' ? null : Number(createForm.capacity);
    if (capacity != null && (Number.isNaN(capacity) || capacity < 0)) {
      setCreateError('Capacity must be a nonnegative number.');
      return;
    }

    setCreateSaving(true);
    setCreateError('');
    const result = await venuesService.createVenue({
      name,
      location,
      country,
      capacity: capacity == null ? null : Math.trunc(capacity),
      twz_location: createForm.twz_location || null,
    });
    setCreateSaving(false);

    if (!result.success) {
      setCreateError(result.error);
      return;
    }

    setVenues((current) => [result.item, ...current]);
    setTotalCount((current) => current + 1);
    setMessage(result.message || 'Venue created.');
    setError('');
    setShowCreateModal(false);
  };

  const handleCellValueChanged = async (params) => {
    const { data, colDef, oldValue, newValue, node } = params;
    const field = colDef.field;

    if (!field || oldValue === newValue) {
      return;
    }

    if (field === 'capacity' && newValue != null && (Number.isNaN(Number(newValue)) || Number(newValue) < 0)) {
      node.setDataValue(field, oldValue);
      setError('Capacity must be a nonnegative number.');
      return;
    }

    const result = await venuesService.updateVenue(data.venue_id, {
      [field]: newValue === '' ? null : newValue,
    });

    if (!result.success) {
      node.setDataValue(field, oldValue);
      setError(result.error);
      return;
    }

    setError('');
    setMessage(result.message || 'Venue updated.');
    setVenues((current) => current.map((item) => (
      item.venue_id === result.item.venue_id ? result.item : item
    )));
    node.setData(result.item);
  };

  const handleDeleteVenue = async (venue) => {
    if (!venue?.venue_id) return;
    setContextMenu(null);
    const confirmed = window.confirm(`Delete venue "${venue.name}"? This will remove its aliases and unlink it from existing records.`);
    if (!confirmed) return;

    const result = await venuesService.deleteVenue(venue.venue_id);
    if (!result.success) {
      setError(result.error);
      return;
    }

    setVenues((current) => current.filter((item) => item.venue_id !== venue.venue_id));
    setTotalCount((current) => Math.max(0, current - 1));
    setMessage(result.message || 'Venue deleted.');
    setError('');
  };

  const openMergePanel = (venue) => {
    if (!venue?.venue_id) return;
    setContextMenu(null);
    setMergePanel({ venue, saving: false });
    setMergeQuery('');
    setMergeResults([]);
    setMergeError('');
  };

  const closeMergePanel = () => {
    if (mergePanel?.saving) return;
    setMergePanel(null);
    setMergeQuery('');
    setMergeResults([]);
    setMergeError('');
  };

  const handleMergeVenue = async (targetVenue) => {
    if (!mergePanel?.venue?.venue_id || !targetVenue?.venue_id) return;

    const confirmed = window.confirm(`Merge "${mergePanel.venue.name}" into "${targetVenue.name}"?`);
    if (!confirmed) return;

    setMergePanel((current) => current ? { ...current, saving: true } : current);
    setMergeError('');
    const result = await venuesService.mergeVenue(mergePanel.venue.venue_id, targetVenue.venue_id);

    if (!result.success) {
      setMergePanel((current) => current ? { ...current, saving: false } : current);
      setMergeError(result.error);
      return;
    }

    setVenues((current) => current
      .filter((item) => item.venue_id !== result.mergedVenueId)
      .map((item) => (item.venue_id === result.item.venue_id ? result.item : item)));
    setTotalCount((current) => Math.max(0, current - 1));
    setMessage(result.message || 'Venue merged.');
    setError('');
    closeMergePanel();
  };

  const openAliasPanel = async (venue) => {
    if (!venue?.venue_id) return;
    setContextMenu(null);
    setAliasPanel({
      venue,
      aliases: [],
      error: '',
    });
    setAliasInput('');
    setAliasLoading(true);
    const result = await venuesService.listAliases(venue.venue_id);
    setAliasLoading(false);

    if (!result.success) {
      setAliasPanel((current) => current ? { ...current, error: result.error } : current);
      return;
    }

    setAliasPanel((current) => current ? {
      ...current,
      aliases: result.items || [],
      error: '',
    } : current);
  };

  const handleAddAlias = async (event) => {
    event.preventDefault();
    if (!aliasPanel?.venue?.venue_id || !aliasInput.trim()) return;

    setAliasLoading(true);
    const result = await venuesService.addAlias(aliasPanel.venue.venue_id, aliasInput.trim());
    setAliasLoading(false);

    if (!result.success) {
      setAliasPanel((current) => current ? { ...current, error: result.error } : current);
      return;
    }

    setAliasInput('');
    setMessage(result.message || 'Alias added.');
    setAliasPanel((current) => current ? {
      ...current,
      aliases: [...current.aliases, result.item],
      error: '',
    } : current);
    setVenues((current) => current.map((item) => (
      item.venue_id === aliasPanel.venue.venue_id
        ? { ...item, alias_count: (item.alias_count || 0) + 1 }
        : item
    )));
  };

  const handleCellContextMenu = (params) => {
    if (!params.data?.venue_id) {
      setContextMenu(null);
      return;
    }

    params.event.preventDefault();
    setContextMenu({
      x: params.event.clientX,
      y: params.event.clientY,
      venue: params.data,
    });
  };

  return (
    <div className="venues-page resource-page-with-sidebar">
      <ResourceSidebar />
      <header className="venues-header">
        <div className="venues-header-content">
          <HeaderNewTabButton />
          <h1>Venues</h1>
          <div className="venues-header-actions">
            <button className="btn btn-primary" type="button" onClick={openCreateModal}>
              Add Venue
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/user')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="venues-main">
        <form className="venues-filter-form" onSubmit={handleFilter}>
          <div className="form-group">
            <label htmlFor="venues-query">Search</label>
            <input
              id="venues-query"
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="venue, location, country, alias..."
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>

        <div className="venues-summary">
          <strong>Showing:</strong> {venues.length} venues
          {!!totalCount && <span className="venues-summary-tag">Total: {totalCount}</span>}
        </div>

        {error && <div className="notification error">{error}</div>}
        {message && <div className="notification success">{message}</div>}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading venues...</p>
          </div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid">
              <AgGridReact
                theme="legacy"
                rowData={venues}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows={true}
                alwaysShowHorizontalScroll={true}
                pagination={true}
                paginationPageSize={100}
                getRowId={(params) => params.data.venue_id}
                onCellValueChanged={handleCellValueChanged}
                onCellContextMenu={handleCellContextMenu}
                preventDefaultOnContextMenu
                onCellDoubleClicked={(params) => openAliasPanel(params.data)}
              />
            </div>
          </div>
        )}

        {contextMenu && (
          <div
            className="venues-context-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(event) => event.stopPropagation()}
          >
            <button className="venues-context-menu-item" type="button" onClick={() => openAliasPanel(contextMenu.venue)}>
              Show Aliases
            </button>
            <button className="venues-context-menu-item" type="button" onClick={() => openAliasPanel(contextMenu.venue)}>
              Add Alias
            </button>
            <button className="venues-context-menu-item" type="button" onClick={() => openMergePanel(contextMenu.venue)}>
              Merge Into...
            </button>
            <button className="venues-context-menu-item venues-context-menu-danger" type="button" onClick={() => handleDeleteVenue(contextMenu.venue)}>
              Delete
            </button>
          </div>
        )}

        {aliasPanel && (
          <aside className="venues-alias-panel">
            <div className="venues-alias-panel-header">
              <div>
                <h2>{aliasPanel.venue.name}</h2>
                <p>{aliasPanel.aliases.length} aliases</p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setAliasPanel(null)}>
                Close
              </button>
            </div>

            <form className="venues-alias-form" onSubmit={handleAddAlias}>
              <input
                type="text"
                value={aliasInput}
                onChange={(event) => setAliasInput(event.target.value)}
                placeholder="Add alias..."
                disabled={aliasLoading}
              />
              <button className="btn btn-primary" type="submit" disabled={aliasLoading || !aliasInput.trim()}>
                Add
              </button>
            </form>

            {aliasPanel.error && <div className="notification error">{aliasPanel.error}</div>}
            {aliasLoading && <div className="venues-alias-empty">Loading aliases...</div>}
            {!aliasLoading && !aliasPanel.aliases.length && (
              <div className="venues-alias-empty">No aliases yet.</div>
            )}
            {!aliasLoading && !!aliasPanel.aliases.length && (
              <div className="venues-alias-list">
                {aliasPanel.aliases.map((alias) => (
                  <div className="venues-alias-row" key={alias.venue_mapping_id}>
                    <strong>{alias.alias_name}</strong>
                    <span>{alias.source || '-'}</span>
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}

        {mergePanel && (
          <div className="venues-modal-backdrop" onMouseDown={closeMergePanel}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <div>
                  <h2>Merge Venue</h2>
                  <p className="venues-modal-subtitle">{mergePanel.venue.name}</p>
                </div>
                <button className="venues-modal-close" type="button" onClick={closeMergePanel} disabled={mergePanel.saving}>
                  x
                </button>
              </div>

              <div className="venues-merge-body">
                <div className="form-group">
                  <label htmlFor="venue-merge-query">Merge Into</label>
                  <input
                    id="venue-merge-query"
                    type="text"
                    value={mergeQuery}
                    onChange={(event) => setMergeQuery(event.target.value)}
                    placeholder="Search target venue..."
                    disabled={mergePanel.saving}
                    autoFocus
                  />
                </div>

                {mergeError && <div className="notification error">{mergeError}</div>}
                {mergeLoading && <div className="venues-alias-empty">Searching venues...</div>}
                {!mergeLoading && mergeQuery.trim() && !mergeResults.length && (
                  <div className="venues-alias-empty">No target venues found.</div>
                )}
                {!mergeLoading && !!mergeResults.length && (
                  <div className="venues-merge-results">
                    {mergeResults.map((venue) => (
                      <button
                        key={venue.venue_id}
                        className="venues-merge-result"
                        type="button"
                        onClick={() => handleMergeVenue(venue)}
                        disabled={mergePanel.saving}
                      >
                        <strong>{venue.name}</strong>
                        <span>{[venue.location, venue.country, venue.twz_location].filter(Boolean).join(' | ') || '-'}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeMergePanel} disabled={mergePanel.saving}>
                    Cancel
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

        {showCreateModal && (
          <div className="venues-modal-backdrop" onMouseDown={closeCreateModal}>
            <section className="venues-modal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="venues-modal-header">
                <h2>Add Venue</h2>
                <button className="venues-modal-close" type="button" onClick={closeCreateModal} disabled={createSaving}>
                  x
                </button>
              </div>

              <form className="venues-modal-form" onSubmit={handleCreateVenue}>
                <div className="form-group">
                  <label htmlFor="venue-name">Venue Name</label>
                  <input
                    id="venue-name"
                    type="text"
                    value={createForm.name}
                    onChange={(event) => handleCreateFormChange('name', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue-location">Location</label>
                  <input
                    id="venue-location"
                    type="text"
                    value={createForm.location}
                    onChange={(event) => handleCreateFormChange('location', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue-country">Country</label>
                  <input
                    id="venue-country"
                    type="text"
                    value={createForm.country}
                    onChange={(event) => handleCreateFormChange('country', event.target.value)}
                    disabled={createSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue-capacity">Capacity</label>
                  <input
                    id="venue-capacity"
                    type="number"
                    min="0"
                    step="1"
                    value={createForm.capacity}
                    onChange={(event) => handleCreateFormChange('capacity', event.target.value)}
                    disabled={createSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue-twz-location">TWZ Location</label>
                  <select
                    id="venue-twz-location"
                    value={createForm.twz_location}
                    onChange={(event) => handleCreateFormChange('twz_location', event.target.value)}
                    disabled={createSaving}
                  >
                    <option value="">Select...</option>
                    {TWZ_LOCATIONS.map((location) => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                {createError && <div className="notification error">{createError}</div>}

                <div className="venues-modal-actions">
                  <button className="btn btn-secondary" type="button" onClick={closeCreateModal} disabled={createSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={createSaving}>
                    {createSaving ? 'Saving...' : 'Save Venue'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default VenuesPage;
